import { useMemo, useState, useEffect } from "react";

import type { NullableMapping, NullableMappings } from "@hz/common/api-types/board";
import * as constants from "@hz/common/constants";
import type { EntityType } from "@hz/common/constants";
import type { Mappings, SubmitDataMappingParams } from "@hz/common/api-types/board";

import { ArrowLeftRight, Info } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetDataMappingQuery, useSubmitDataMappingMutation } from "@state/apiSlice/requests";

import { Box, Center, Flex, Group, HStack, Icon, Separator, Spinner, Table, Text, VStack } from "@chakra-ui/react";

import { Button } from "@components/ui/button";
import { toaster } from "@components/ui/toaster";
import { Tooltip } from "@components/ui/tooltip";

import { fetchInit, selectInit, selectMondayBoardId } from "@state/mondaySlice";
import type { AppDispatch } from "@state/store";

import MappingSelector from "./components/MappingsTable";
import { useSearchParams } from "react-router-dom";
import { useGmcMappingOptionsQuery } from "@state/apiSlice/requests";

type MappingsPageProps = {
  isOnboarding?: boolean;
  onGoBackHandler?: () => void;
  onSaveCallback?: () => void;
};

function MappingsPage({ isOnboarding, onGoBackHandler, onSaveCallback }: MappingsPageProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const mondayBoardId = useSelector(selectMondayBoardId) as number;

  const [searchParams] = useSearchParams();
  const showCloseButton = searchParams.get("showCloseButton") === "true";

  const {data: existingData, isLoading: isExistingMappingLoading } = useGetDataMappingQuery({boardId: mondayBoardId})

  const { data: fieldOptions, isLoading: isGettingOptions } = useGmcMappingOptionsQuery(
    { boardId: mondayBoardId },
    { skip: !mondayBoardId }
  );



  const [submitDataMapping, { isLoading: isSubmitting }] = useSubmitDataMappingMutation();


  const columns = useMemo(() => fieldOptions?.mondayColumns || [], [fieldOptions]);

  const [entity, setEntity] = useState<EntityType>(constants.GMC);
  const [boardName, setBoardName] = useState<string>();

  const [mappings, setMappings] = useState<NullableMappings>({});

  console.log(mappings)

useEffect(() => {
  if (existingData?.mapping) {
    const sanitizedMapping: NullableMappings = Object.fromEntries(
      Object.entries(existingData.mapping).map(([columnId, field]) => {
        if (!field) return [columnId, null];
        const { id, name } = field;
        return [columnId, { id, name }];
      })
    );

    setMappings(sanitizedMapping);
  }
}, [existingData]);

  const handleMappingChange = (columnId: string, value: NullableMapping) => {
    setMappings((prevMappings) => ({ ...prevMappings, [columnId]: value }));
  };

  const canSaveMappings = useMemo(() => Object.values(mappings).some((value) => value), [mappings]);

  if (isGettingOptions || isExistingMappingLoading) {
  return (
    <Center mt="100px">
      <Spinner size="lg" />
    </Center>
  );
}



  const saveMappingHandler = async () => {
    if (!mondayBoardId || !entity) return;

    const sanitizedMappings = Object.fromEntries(Object.entries(mappings).filter(([, value]) => value)) as Mappings;

    const payload: SubmitDataMappingParams = {
      boardId: mondayBoardId,
      entityType: entity,
      mappings: sanitizedMappings,
    };

    console.log(payload);

    try {
      await submitDataMapping(payload).unwrap();

      if (onSaveCallback) {
        onSaveCallback();
      } else {
        toaster.success({
          title: "Success! Google records are now syncing with this monday board.",
          description: "Initial sync may take up to 2 hours.",
          duration: 5000,
        });

        await dispatch(fetchInit({ forceRefetch: true }));
        navigate("/", { replace: true });
      }
    } catch {
      toaster.error({
        title: "Failed to save data mapping!",
        description: "Please try again...",
      });
    }
  };

  const Body = () => {
    return (
      <Flex align="start" direction="column" flex="1" w="full">
        <MappingsPageHeader />

        <VStack gap="24px" align="stretch" w="full">
          <Box>
            <Box
              minHeight="250px"
              maxHeight="calc(100vh - 550px)"
              overflowY="auto"
              borderWidth="1px"
              borderRadius="md"
              position="relative"
            >
              {isGettingOptions && (
                <Center position="absolute" top="0" left="0" width="100%" height="100%" bg="rgba(255, 255, 255, 0.8)" zIndex="1">
                  <Spinner size="lg" />
                </Center>
              )}
              <Table.Root size="sm" stickyHeader>
                <Table.Header>
                  <Table.Row bg="bg.subtle">
                    <Table.ColumnHeader width="40%">{boardName || "Board"} Columns</Table.ColumnHeader>
                    <Table.ColumnHeader width="10%" textAlign="center" />
                    <Table.ColumnHeader width="50%">
                      <HStack gap={1}>
                        Google Merchant Center
                        <Tooltip
                          content={`Assign each supported GMC data point to the relevant column on the ${boardName} board for seamless data integration.`}
                          openDelay={100}
                          closeDelay={100}
                          showArrow
                          arrowStyle={{ left: 16 }}
                          positioning={{ offset: { mainAxis: 3 } }}
                        >
                          <Icon color="gray.600" size="xs">
                            <Info />
                          </Icon>
                        </Tooltip>
                      </HStack>
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {entity &&
                    columns.map((column, index) => {
                      const compatibleGmcFields =
                        fieldOptions?.gmcFields?.filter((x) => x.acceptedTypes.includes(column.type)) || [];

                      return (
                        <Table.Row key={column.id}>
                          <Table.Cell borderBottom={index === columns.length - 1 ? "none" : ""} p={2} width="40%">
                            <Text fontSize="sm">{column.title}</Text>
                          </Table.Cell>
                          <Table.Cell
                            py="0px"
                            textAlign="center"
                            width="10%"
                            borderBottom={index === columns.length - 1 ? "none" : ""}
                          >
                            <Center>
                              <Icon color="gray.300" size="sm">
                                <ArrowLeftRight />
                              </Icon>
                            </Center>
                          </Table.Cell>
                          <Table.Cell borderBottom={index === columns.length - 1 ? "none" : ""} px="2px" py="3.5px" width="50%">
                            <MappingSelector
                              mondayColumnId={column.id}
                              entityMap={compatibleGmcFields.map((field) => ({
                                id: field.id,
                                name: field.name,
                              }))}
                              handleFieldChange={handleMappingChange}
                              mappings={mappings}
                            />
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                </Table.Body>
              </Table.Root>
            </Box>
          </Box>

          <HStack justifyContent="flex-end">
            <Group gapX={2} bg="white">
              {onGoBackHandler && (
                <Button variant="outline" onClick={onGoBackHandler} disabled={isSubmitting || isGettingOptions}>
                  Go back
                </Button>
              )}

                 {showCloseButton && (
                <Button variant="outline" onClick={() => navigate("/")}>
                  Cancel
                </Button>
              )}

              <Button
                loading={isSubmitting}
                disabled={isSubmitting || isGettingOptions || !entity || !canSaveMappings}
                colorPalette="blue"
                onClick={saveMappingHandler}
              >
                {isOnboarding ? "Complete setup" : "Save mapping"}
              </Button>
            </Group>
          </HStack>
        </VStack>
      </Flex>
    );
  };

  return (
    <>
      {isOnboarding ? (
        <Body />
      ) : (
        <Flex justifyContent="center" my="24px">
          <Flex align="start" direction="column" flex="1" w="full" maxW="2xl">
            <Body />
          </Flex>
        </Flex>
      )}
    </>
  );
}

export const MappingsPageHeader = () => (
  <>
    <Text fontSize="lg" fontWeight="medium">
      Mappings
    </Text>
    <Text fontSize="sm" color="gray.600">
      Manage data mappings between GMC and the current monday board.
    </Text>

    <Separator my="24px" w="full" />
  </>
);

export default MappingsPage;
